import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TrashIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { Page, PageHeader, Skeleton, InferenceTabs, INFERENCE_EYEBROW } from '../components/console/ui';
import { KeyIc } from '../components/console/icons';
import toast from 'react-hot-toast';
import { apiTokenService, TokenExpiry } from '../services/authApi';
import { inferenceService, InferenceApiKey } from '../services/inferenceApi';
import { joinKeys, KeyRow } from './apiKeys.join';
import { copyText } from '../utils/clipboard';

// Surface the backend's own error message (e.g. "Maximum of 5 API tokens
// allowed. Please revoke an existing token." on 402) instead of a generic one.
function serverMsg(e: any, fallback: string): string {
  return e?.response?.data?.message || e?.response?.data?.error || fallback;
}

const EXPIRY_OPTIONS: { label: string; value: TokenExpiry }[] = [
  { label: 'Never', value: 'never' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
  { label: '1 year', value: '365d' },
];

export default function ApiKeys() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newExpiry, setNewExpiry] = useState<TokenExpiry>('never');
  const [revealed, setRevealed] = useState<string | null>(null);

  const tokensQ = useQuery({ queryKey: ['api-tokens'], queryFn: () => apiTokenService.list().then((r) => r.data) });
  const settingsQ = useQuery({ queryKey: ['inference-keys'], queryFn: () => inferenceService.listApiKeys().then((r) => r.data) });

  const tokens = tokensQ.data?.data ?? [];
  const settings: InferenceApiKey[] = settingsQ.data?.data ?? [];
  const rows: KeyRow[] = joinKeys(tokens, settings);

  const createM = useMutation({
    mutationFn: () => apiTokenService.create(newName.trim(), newExpiry).then((r) => r.data),
    onSuccess: (data) => {
      setRevealed(data.token ?? '');
      setNewName('');
      setNewExpiry('never');
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['api-tokens'] });
      qc.invalidateQueries({ queryKey: ['inference-keys'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not create key')),
  });

  const revokeM = useMutation({
    mutationFn: (id: string) => apiTokenService.revoke(id),
    onSuccess: () => {
      toast.success('Key revoked');
      qc.invalidateQueries({ queryKey: ['api-tokens'] });
      qc.invalidateQueries({ queryKey: ['inference-keys'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not revoke key')),
  });

  const copy = (v: string) => {
    void copyText(v);
  };

  const openCreate = () => setShowCreate(true);

  return (
    <Page>
      <PageHeader
        eyebrow={INFERENCE_EYEBROW}
        title="API keys"
        subtitle="Keys authenticate requests to the Podstack Inference API. Each key gets its own expiry, monthly token limit and data settings."
        actions={
          <button type="button" className="ps-btn" onClick={openCreate}>
            Create key
          </button>
        }
      />

      <InferenceTabs />

      {revealed !== null && (
        <section className="ps-card flex flex-col gap-3 border-ps-accent-line bg-[#140C06] p-5" aria-label="New API key">
          <p className="m-0 text-sm font-medium text-primary-300">Copy your key now - it won't be shown again.</p>
          <div className="ps-copy">
            <code className="text-ps-text">{revealed}</code>
            <button type="button" className="ps-cb" onClick={() => copy(revealed)} aria-label="Copy key">
              <ClipboardIcon className="h-4 w-4" /> Copy
            </button>
          </div>
          <div>
            <button className="ps-link text-xs" onClick={() => setRevealed(null)}>
              Dismiss
            </button>
          </div>
        </section>
      )}

      {showCreate && (
        <section
          className="ps-card flex flex-col gap-4 border-[#5B2A0E] bg-[#140C06] p-5"
          aria-labelledby="new-key-heading"
        >
          <h2 id="new-key-heading" className="m-0 text-[17px] font-medium">
            New API key
          </h2>
          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr))]">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-ps-muted">Name</span>
              <input
                className="input"
                placeholder="Key name (e.g. production)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-ps-muted">Expires</span>
              <select className="input" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value as TokenExpiry)}>
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.label} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="m-0 text-xs text-ps-subtle">
            The key (psk_…) is shown once after creation - copy it into your secrets manager. You can set a monthly
            token limit once it's created.
          </p>
          <div className="flex justify-end gap-2.5">
            <button className="ps-btn2" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
            <button className="ps-btn" disabled={!newName.trim() || createM.isPending} onClick={() => createM.mutate()}>
              {createM.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </section>
      )}

      {tokensQ.isLoading ? (
        <div className="ps-card flex flex-col gap-3 p-5" aria-label="Loading keys…">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-1/2" />
          <span className="sr-only">Loading keys…</span>
        </div>
      ) : tokensQ.isError ? (
        <div className="ps-card px-6 py-14 text-center text-sm text-ps-err">Could not load API keys.</div>
      ) : rows.length === 0 ? (
        <section
          className="ps-card flex flex-col items-center gap-3 px-6 py-9 text-center"
          aria-labelledby="no-keys-heading"
        >
          <span className="ps-num h-12 w-12">
            <KeyIc className="h-[22px] w-[22px]" />
          </span>
          <h2 id="no-keys-heading" className="m-0 text-lg font-medium">
            No API keys yet
          </h2>
          <p className="m-0 max-w-[460px] text-[13px] leading-relaxed text-ps-muted">
            The playground works with your login. Create a key to start making requests from code.
          </p>
          {!showCreate && (
            <button type="button" className="ps-btn" onClick={openCreate}>
              Create your first key
            </button>
          )}
        </section>
      ) : (
        <div className="ps-card overflow-x-auto">
          <table className="ps-tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Monthly token limit</th>
                <th>Store data</th>
                <th>Cache</th>
                <th>Last used</th>
                <th>Expires</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                // Key includes the settings id so the row remounts (re-seeding its
                // local limit state) once the inference-side settings row arrives.
                <KeyRowView
                  key={`${row.token.id}:${row.settings?.id ?? 'pending'}`}
                  row={row}
                  onRevoke={() => {
                    if (window.confirm(`Revoke "${row.token.name}"? Requests using this key will stop working immediately.`)) {
                      revokeM.mutate(row.token.id);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
          <div className="px-3.5 py-2.5 text-xs text-ps-subtle">
            Leave the monthly limit blank for unlimited. Requests over a limit get a 429.
          </div>
        </div>
      )}
    </Page>
  );
}

function KeyRowView({ row, onRevoke }: { row: KeyRow; onRevoke: () => void }) {
  const qc = useQueryClient();
  const s = row.settings;
  const [limit, setLimit] = useState<string>(s?.monthly_token_limit != null ? String(s.monthly_token_limit) : '');

  const saveM = useMutation({
    mutationFn: (patch: Record<string, unknown>) => inferenceService.updateApiKeyLimits(s!.id, patch),
    onSuccess: () => {
      toast.success('Settings saved');
      qc.invalidateQueries({ queryKey: ['inference-keys'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not save settings')),
  });

  return (
    <tr>
      <td>
        <div className="font-medium text-ps-text">{row.token.name}</div>
        <div className="text-xs text-ps-subtle">Created {new Date(row.token.created_at).toLocaleDateString()}</div>
      </td>
      <td>
        <code className="text-ps-muted">{row.token.prefix}…</code>
      </td>
      {s ? (
        <>
          <td>
            <input
              className="input !w-40 !min-h-[34px]"
              type="number"
              aria-label={`Monthly token limit for ${row.token.name} (blank = unlimited)`}
              placeholder="Unlimited"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              onBlur={() => {
                const current = s?.monthly_token_limit ?? 0;
                const next = limit.trim() === '' ? 0 : Number(limit);
                // Skip when unchanged (don't wipe the quota on a stray blur)
                // and never PATCH NaN from unparseable input.
                if (Number.isNaN(next) || next === current) return;
                saveM.mutate({ monthly_token_limit: next });
              }}
            />
          </td>
          <td>
            <ToggleField
              label={`Store request data for ${row.token.name}`}
              checked={s.store_data}
              onChange={(v) => saveM.mutate({ store_data: v })}
            />
          </td>
          <td>
            <ToggleField
              label={`Response cache for ${row.token.name}`}
              checked={s.cache_enabled}
              onChange={(v) => saveM.mutate({ cache_enabled: v })}
            />
          </td>
        </>
      ) : (
        <td colSpan={3} className="text-xs text-ps-subtle">
          Per-key settings available shortly.
        </td>
      )}
      <td className="text-ps-muted">{s?.last_used_at ? new Date(s.last_used_at).toLocaleDateString() : '-'}</td>
      <td className="text-ps-muted">
        {row.token.expires_at ? new Date(row.token.expires_at).toLocaleDateString() : '-'}
      </td>
      <td className="text-right">
        <button
          className="ps-btn-danger ps-btn-sm"
          onClick={onRevoke}
          aria-label={`Revoke ${row.token.name}`}
          title="Revoke key"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-[18px] w-[18px] accent-primary-500"
    />
  );
}
